import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { mapValues, mapValuesEqual } from '@common/util/map';
import { isEqual } from 'lodash';
import { getVersion } from 'prosemirror-collab';
import { ProseMirrorNode } from 'prosemirror-model';
import { EditorState, EditorStateConfig, Plugin, PluginKey, Selection, Transaction } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { Mapping } from 'prosemirror-transform';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';

export const CollabUsersPluginKey: PluginKey = new PluginKey('collab-users');

export interface CollabUsersPluginOptions {
  clientId: string;
  collabUsers?: CollabUserJSON[];
  document?: Document;
  userId: string;
}

const userStateCheckDelayMS: number = 10000;
const userIdlePeriodMS: number = 30000;
const userJustAddedPeriodMS: number = 5000;

/**
 * Returns true if there are user updates to send to the collab authority.
 * @param editorState The current editor state.
 * @returns True if there are user updates to send.
 */
export function hasSendableCollabUser(editorState: EditorState): boolean {
  const collabUsersState: CollabUsersState = CollabUsersPluginKey.getState(editorState);
  const editorSelectionJSON = editorState.selection.toJSON();
  const currentUser = collabUsersState.userMap.get(collabUsersState.clientId);

  // If the current user isn't in the collab user data yet OR the current user's selection has changed THEN there are updates to send
  return !currentUser?.collabUser?.selection || !isEqual(editorSelectionJSON, currentUser.collabUser.selection);
}

/**
 * Adds the collab user update to a transaction if it is not already in the editor state.
 * Applying this transaction moves the state of the collab users plugin forward to match the authority's state.
 * Call this after successfully sending the collab user update to the authority or after receiving the collab user update from the authority.
 * @param tr The transaction to add the collab user update to.
 * @param editorState The current editor state.
 * @param collabUsers The collab users to update the state with.
 * @param options The options for the update. Specify 'add' to add/update the collab users in the state. Specify 'set' to replace the collab users in the state.
 * @returns If the state needs updating to match the collab users then the transaction is returned. Otherwise, undefined is returned.
 */
export function receiveCollabUserTransaction(tr: Transaction, editorState: EditorState, collabUsers: CollabUserJSON[], options?: { action: 'add' | 'set' }): Transaction {
  const collabUsersState: CollabUsersState = CollabUsersPluginKey.getState(editorState);
  const action = options?.action ?? 'set';
  let stateNeedsUpdating = false;

  if (action === 'add') {
    // The state needs updating if the added user is not in the state or is different than the state
    stateNeedsUpdating = collabUsers.some(collabUser => {
      const stateUser = collabUsersState.userMap.get(collabUser.clientId);
      return !stateUser || !isEqual(stateUser.collabUser, collabUser);
    });
  } else if (action === 'set') {
    // The state needs updating if anything is different between the collab users and the state
    stateNeedsUpdating = !mapValuesEqual(collabUsersState.userMap, collabUsers, collabUser => collabUser.clientId, (collabUserState, collabUser) => {
      return isEqual(collabUserState.collabUser, collabUser);
    });
  }

  if (stateNeedsUpdating) {
    if (action === 'add') {
      // Create a map of the users for easy updating
      const collabUserMap = mapValues(collabUsersState.userMap, collabUserState => collabUserState.collabUser) as Map<string, CollabUserJSON>;
      collabUsers.forEach(collabUser => collabUserMap.set(collabUser.clientId, collabUser));
      collabUsers = Array.from(collabUserMap.values());
    }

    tr.setMeta('collab-user-update', collabUsers);
    return tr;
  }
}

/**
 * Creates a cursor decoration.
 * @param pos The position to create the cursor decoration at.
 * @param userName The user's name to display in the cursor tooltip.
 * @param themeNumber The theme number to use for the cursor.
 * @param showUserNameTooltip Whether or not to show the cursor user name tooltip.
 * @param document Optionally provide the document to create the cursor decoration with. Defaults to window.document. Primarily used for running tests in Node.
 */
function createCursorDecoration(pos: number, userName: string, themeNumber: number, showUserNameTooltip: boolean, document?: Document): Decoration {
  if (!document && typeof window !== 'undefined') {
    document = window.document;
  }

  const cursor = document.createElement('span');
  cursor.className = `mc-editor-collab-user-cursor mc-editor-collab-user-cursor-theme-${themeNumber}${showUserNameTooltip ? ' mc-editor-collab-user-show-tooltip' : ''}`;
  cursor.setAttribute('data-user-name', userName);

  return Decoration.widget(pos, cursor);
}

function createSelectionDecoration(from: number, to: number, themeNumber: number): Decoration {
  return Decoration.inline(from, to, { class: `mc-editor-collab-user-text-selection-theme-${themeNumber}` });
}

function createNodeDecoration(from: number, to: number, userName: string, themeNumber: number, showUserNameTooltip: boolean): Decoration {
  return Decoration.node(from, to, {
    class: `mc-editor-collab-user-node-selection mc-editor-collab-user-node-selection-theme-${themeNumber}${showUserNameTooltip ? ' mc-editor-collab-user-show-tooltip' : ''}`,
    'data-user-name': userName
  });
}

function createCellDecoration(from: number, to: number, userName: string, themeNumber: number, showUserNameTooltip: boolean): Decoration {
  return Decoration.node(from, to, {
    class: `mc-editor-collab-user-cell-selection mc-editor-collab-user-cell-selection-theme-${themeNumber}${showUserNameTooltip ? ' mc-editor-collab-user-show-tooltip' : ''}`,
    'data-user-name': userName
  });
}

/**
 * Returns true if the user name tooltip should be shown for the given collab user state.
 * The user name tooltip is shown if the user was just added and there hasn't been a local edit since they were added.
 * @param collabUserstate The collab user state to check.
 * @param lastLocalEditAt The last local edit time to check against.
 * @returns True if the user name tooltip should be shown.
 */
function shouldShowUserNameTooltip(collabUserstate: CollabUserState, lastLocalEditAt: Date): boolean {
  // Show the user name tooltip if they were just added and there hasn't been a local edit since they were added
  if (lastLocalEditAt) {
    return collabUserstate.justAdded && lastLocalEditAt.getTime() <= new Date(collabUserstate.addedAt).getTime();
  } else {
    return collabUserstate.justAdded;
  }
}

/**
 * Maps the selection to the new doc using the mapping.
 * If the selection mapping fails then it will return a near selection instead.
 * This is to workaround an error from being thrown when the selection is a node selection and the node has been dragged and dropped.
 * @param selection The selection to map.
 * @param newDoc The new doc to map the selection to.
 * @param mapping The mapping to use to map the selection.
 * @returns The mapped selection.
 */
function mapSelection(selection: Selection, newDoc: ProseMirrorNode, mapping: Mapping): Selection {
  // Mapping a node selection from a drag and drop is causing an error to be thrown in NodeSelection.map
  // So catch the error and return a near selection instead. Which is what ProseMirror would normally do if the mapped-to position was deleted.
  // This may be a bug in ProseMirror and could be worth reporting.
  // If the mapping does not match the actual selection in the other user's editor then it will be fixed when the user updates their selection in the collab doc.
  try {
    return selection.map(newDoc, mapping);
  } catch (ex) {
    const { pos } = mapping.mapResult(selection.anchor);
    return Selection.near(newDoc.resolve(pos));
  }
}

/**
 * Maps the collab user selection data to the new doc using the mapping.
 * Also sets the localDocVersion to the new doc version.
 * @param userMap The current state of the collab users.
 * @param oldDoc The old doc to map the user data from.
 * @param newDoc The new doc to map the user data to.
 * @param mapping The mapping to use to map the user data.
 * @param newDocVersion The new doc version to set the localDocVersion to.
 * @returns The new user map with the updated collab user data.
 */
function mapCollabUserMap(collabUsersState: CollabUsersState, userMap: ReadonlyMap<string, CollabUserState>, oldDoc: ProseMirrorNode, newDoc: ProseMirrorNode, mapping: Mapping, newDocVersion: number): ReadonlyMap<string, CollabUserState> {
  const newUserMap = new Map<string, CollabUserState>();

  userMap.forEach((collabUserState, clientId) => {
    let newCollabUserState: CollabUserState;

    // If this is the current user then skip mapping the selection
    if (clientId === collabUsersState.clientId) {
      newCollabUserState = collabUserState;
    } else {
      newCollabUserState = {
        ...collabUserState,
        localDocVersion: newDocVersion,
        collabUser: {
          ...collabUserState.collabUser,
          selection: collabUserState.collabUser.selection ? mapSelection(Selection.fromJSON(oldDoc, collabUserState.collabUser.selection), newDoc, mapping).toJSON() : collabUserState.collabUser.selection
        }
      };
    }

    newUserMap.set(clientId, newCollabUserState);
  });

  return newUserMap;
}

/**
 * Creates a new user map by updating the current collab users state with the new collab users data.
 * Assigns theme numbers to new users returning the next available theme number.
 * @param collabUsersState The current state of the collab users.
 * @param collabUsers The new collab users data.
 * @returns The new theme number and the new user map.
 */
function createCollabUserMap(collabUsersState: CollabUsersState, collabUsers: CollabUserJSON[]): { themeNumber: number, userMap: ReadonlyMap<string, CollabUserState> } {
  const userMap = new Map<string, CollabUserState>();
  let themeNumber = collabUsersState.themeNumber;

  for (const collabUser of collabUsers) {
    const collabUserState = collabUsersState.userMap.get(collabUser.clientId);

    // Determine the theme number for the user
    let userThemeNumber = collabUserState?.themeNumber;
    if (typeof userThemeNumber !== 'number') {
      userThemeNumber = themeNumber++;
      if (themeNumber > 10) {
        themeNumber = 1;
      }
    }

    // Set the user data using the current state if it exists
    userMap.set(collabUser.clientId, {
      addedAt: collabUserState?.addedAt ?? new Date().toISOString(),
      collabUser,
      idle: collabUserState?.idle ?? false,
      justAdded: collabUserState?.justAdded ?? true,
      localDocVersion: collabUserState?.localDocVersion,
      themeNumber: userThemeNumber
    });
  }

  return { themeNumber, userMap };
}

/**
 * Creates decorations for the collab user cursors and selections using the userMap data.
 * The user's idle and justAdded states are updated and returned in a new userMap.
 * @param doc The doc to create the decorations for.
 * @param collabUsersState The state of the collab users.
 * @param userMap The new collab user data.
 * @param lastLocalEditAt The last local edit time to determine if the user name tooltip should be shown.
 * @returns The decorations and the new userMap.
 */
function createCursorDecorations(doc: ProseMirrorNode, collabUsersState: CollabUsersState, userMap: ReadonlyMap<string, CollabUserState>, lastLocalEditAt: Date, document?: Document): { decorations: Decoration[], userMap: ReadonlyMap<string, CollabUserState> } {
  const decorations: Decoration[] = [];

  // Create a new map because the userMap is immutable
  userMap = mapValues(userMap, collabUserState => {
    // Skip the current user
    if (collabUserState.collabUser.clientId === collabUsersState.clientId) {
      return collabUserState;
    }

    // Create a new state for the user since they are immutable
    const newCollabUserState = {
      ...collabUserState
    };

    // Determine if the local selection for the user should be used or the new collab user selection should be used
    const existingCollabUserState = collabUsersState.userMap.get(newCollabUserState.collabUser.clientId);
    if (existingCollabUserState) {
      // If the local doc version is greater than the incoming doc version then use the existing selection as it has been mapped to the latest doc
      if (existingCollabUserState.localDocVersion > newCollabUserState.collabUser.docVersion) {
        newCollabUserState.collabUser.selection = existingCollabUserState?.collabUser.selection;
      }

      // Keep the local doc version
      newCollabUserState.localDocVersion = existingCollabUserState.localDocVersion;
    }

    // Determine the user's idle state
    newCollabUserState.idle = newCollabUserState.collabUser.lastActiveAt ? (new Date().getTime() - new Date(newCollabUserState.collabUser.lastActiveAt).getTime()) >= userIdlePeriodMS : false;

    // Determine if the user was just added
    newCollabUserState.justAdded = newCollabUserState.addedAt ? (new Date().getTime() - new Date(newCollabUserState.addedAt).getTime()) <= userJustAddedPeriodMS : false;

    // Determine if the user name tooltip should be shown
    const showUserNameTooltip = shouldShowUserNameTooltip(newCollabUserState, lastLocalEditAt);

    // Create the proper decorations for the user's selection type
    if (newCollabUserState.collabUser.selection.type === 'text') {
      decorations.push(
        createCursorDecoration(newCollabUserState.collabUser.selection.head, newCollabUserState.collabUser.userName, newCollabUserState.themeNumber, showUserNameTooltip, document)
      );
      // Only show the text selection if the user is not idle
      if (!newCollabUserState.idle) {
        decorations.push(
          createSelectionDecoration(Math.min(newCollabUserState.collabUser.selection.anchor, newCollabUserState.collabUser.selection.head), Math.max(newCollabUserState.collabUser.selection.anchor, newCollabUserState.collabUser.selection.head), newCollabUserState.themeNumber)
        );
      }
    } else if (newCollabUserState.collabUser.selection.type === 'node') {
      const selectedNode = doc.nodeAt(newCollabUserState.collabUser.selection.anchor);
      if (selectedNode) {
        decorations.push(
          createNodeDecoration(newCollabUserState.collabUser.selection.anchor, newCollabUserState.collabUser.selection.anchor + selectedNode.nodeSize, newCollabUserState.collabUser.userName, newCollabUserState.themeNumber, showUserNameTooltip)
        );
      }
    } else if (newCollabUserState.collabUser.selection.type === 'all') {
      decorations.push(
        createNodeDecoration(0, doc.firstChild.nodeSize, newCollabUserState.collabUser.userName, newCollabUserState.themeNumber, showUserNameTooltip)
      );
    } else if (newCollabUserState.collabUser.selection.type === 'cell') {
      const cellSelection = CellSelection.fromJSON(doc, newCollabUserState.collabUser.selection);

      cellSelection.forEachCell((cell, pos) => {
        decorations.push(
          createCellDecoration(pos, pos + cell.nodeSize, newCollabUserState.collabUser.userName, newCollabUserState.themeNumber, showUserNameTooltip)
        );
      });
    }

    return newCollabUserState;
  });

  return {
    decorations,
    userMap
  };
}

/**
 * Returns true if any user's state has changed requiring the decorations to be updated.
 * A user state change can come from whether a user is idle or was recently added to the doc.
 * @param collabUsersState The state of the collab users.
 * @returns True if any user's state has changed.
 */
function anyUserStateChanged(collabUsersState: CollabUsersState): boolean {
  for (const collabUserState of collabUsersState.userMap.values()) {
    // Skip the current user
    if (collabUserState.collabUser.clientId === collabUsersState.clientId) {
      continue;
    }

    // If the user's last active time is at least 30 seconds ago then the user is considered idle
    const userWasIdle = collabUserState.idle;
    const userIsIdle = collabUserState.collabUser.lastActiveAt ? (new Date().getTime() - new Date(collabUserState.collabUser.lastActiveAt).getTime()) >= userIdlePeriodMS : false;

    if (userWasIdle !== userIsIdle) {
      return true;
    }

    // If the user's added time is at least 5 seconds ago then the user is not considered just added
    const userWasJustAdded = collabUserState.justAdded;
    const userIsJustAdded = collabUserState.addedAt ? (new Date().getTime() - new Date(collabUserState.addedAt).getTime()) <= userJustAddedPeriodMS : false;

    if (userWasJustAdded !== userIsJustAdded) {
      return true;
    }
  }

  return false;
}

/**
 * Returns true if the local edit will invalidate the user name tooltip showing for any user.
 * @param collabUsersState The state of the collab users.
 * @param newLastLocalEditAt The new last local edit time.
 * @returns True if the local edit will invalidate the user name tooltip for any user.
 */
function localEditWillInvalidateUserNameTooltip(collabUsersState: CollabUsersState, newLastLocalEditAt: Date): boolean {
  for (const collabUserState of collabUsersState.userMap.values()) {
    // Skip the current user
    if (collabUserState.collabUser.clientId === collabUsersState.clientId) {
      continue;
    }

    // If the state of the showing the user name tooltip is going to change then the decorations need to be updated
    if (shouldShowUserNameTooltip(collabUserState, collabUsersState.lastLocalEditAt) !== shouldShowUserNameTooltip(collabUserState, newLastLocalEditAt)) {
      return true;
    }
  }

  return false;
}

/**
 * The state for a collab user.
 */
interface CollabUserState {
  /** When the user was added to the plugin's state. */
  addedAt: ISO8601DateString;
  /** The collab user's data. */
  readonly collabUser: CollabUserJSON;
  /** Whether or not the collab user is idle. */
  idle: boolean;
  /** Whether or not the collab user was just added to the plugin's state. */
  justAdded: boolean;
  /** The doc version that the user's selection was last mapped through locally. Used to keep the user's cursor in the correct location until the user's final cursor position has been set on the collab doc. */
  localDocVersion: number;
  /** The collab user's theme number for coloring their cursor and selections. */
  themeNumber: number;
}

/**
 * The state for the collab users plugin.
 */
interface CollabUsersState {
  /** The current user's client id. */
  readonly clientId: string,
  /** The decorations for the collab user cursors and selections. */
  readonly decorations: DecorationSet
  /** When the current user last edited the doc. */
  readonly lastLocalEditAt: Date;
  /** The next available theme number to assign to a new collab user. */
  readonly themeNumber: number,
  /** The current user's id. */
  readonly userId: string,
  /** The state of each collab user. */
  readonly userMap: ReadonlyMap<string, CollabUserState>,
}

/**
 * A ProseMirror plugin for tracking and displaying the cursors and selections of the collab users connected to the doc.
 * The plugin's state is a mirror of the state in the collab-authority.
 * When the local state changes, such as the user's selection changing, it is checked against the plugin's state to see if the collab-authority needs to be updated.
 * After updating the collab-authority's state the plugin's state is moved forward to match the collab-authority's state.
 * @param options The options for the plugin. The current user's id and client id are required.
 * @returns The ProseMirror plugin.
 */
export function collabUsersPlugin(options: CollabUsersPluginOptions): Plugin {
  let userStateCheckTimeoutId: any;

  /**
   * Schedules the next user state check by applying a transaction to trigger the check in this plugin.
   * @param editorView The editor view to schedule the state check for.
   */
  function scheduleUserStateCheck(editorView: EditorView) {
    if (!userStateCheckTimeoutId) {
      userStateCheckTimeoutId = setTimeout(() => {
        // Trigger a user state check with a transaction so that the decorations can be updated if necessary
        const tr = editorView.state.tr;
        tr.setMeta('collab-user-state-check', true);
        editorView.dispatch(tr);

        // Clear the timeout id now that it has been executed
        userStateCheckTimeoutId = null;

        // Schedule the next user state check
        scheduleUserStateCheck(editorView);
      }, userStateCheckDelayMS);
    }
  }

  return new Plugin({
    key: CollabUsersPluginKey,

    state: {
      init(config: EditorStateConfig, editorState: EditorState): CollabUsersState {
        // Create the initial empty state which will either be returned or used to build the state for the given collab users
        let collabUsersState: CollabUsersState = {
          clientId: options.clientId,
          decorations: DecorationSet.empty,
          lastLocalEditAt: null,
          themeNumber: 1,
          userId: options.userId,
          userMap: new Map()
        };

        if (options.collabUsers) {
          // Build the state for the collab users
          const newUserData = createCollabUserMap(collabUsersState, options.collabUsers);
          const newDecorationData = createCursorDecorations(editorState.doc, collabUsersState, newUserData.userMap, collabUsersState.lastLocalEditAt, options.document);

          collabUsersState = {
            clientId: options.clientId,
            decorations: DecorationSet.create(editorState.doc, newDecorationData.decorations),
            lastLocalEditAt: null,
            themeNumber: newUserData.themeNumber,
            userId: options.userId,
            userMap: newDecorationData.userMap
          };
        }

        return collabUsersState;
      },
      apply(tr: Transaction, collabUsersState: CollabUsersState, oldEditorState: EditorState, newEditorState: EditorState): CollabUsersState {
        const collabUsers: CollabUserJSON[] = tr.getMeta('collab-user-update');
        let newDecorations = collabUsersState.decorations;
        let newLastLocalEditAt = collabUsersState.lastLocalEditAt;
        let newThemeNumber = collabUsersState.themeNumber;
        let newUserMap = collabUsersState.userMap;

        const isLocalEdit = !tr.getMeta('collab$') && tr.docChanged;
        if (isLocalEdit) {
          // If this is a local edit then update the last local edit time
          newLastLocalEditAt = new Date();
        }

        // If there are new collab users
        if (collabUsers) {
          // Update the decorations for the new collab users
          const newUserData = createCollabUserMap(collabUsersState, collabUsers);
          newThemeNumber = newUserData.themeNumber;

          const newDecorationData = createCursorDecorations(tr.doc, collabUsersState, newUserData.userMap, newLastLocalEditAt, options.document);
          newDecorations = DecorationSet.create(tr.doc, newDecorationData.decorations);
          newUserMap = newDecorationData.userMap;
        }
        // Else if the user state check was triggered
        else if (tr.getMeta('collab-user-state-check')) {
          // Update the decorations if a user's state has changed
          if (anyUserStateChanged(collabUsersState)) {
            const newDecorationData = createCursorDecorations(tr.doc, collabUsersState, collabUsersState.userMap, newLastLocalEditAt, options.document);
            newDecorations = DecorationSet.create(tr.doc, newDecorationData.decorations);
            newUserMap = newDecorationData.userMap;
          }
        }
        // Else if the new last local edit time is going to change whether user name tooltips are shown
        else if (localEditWillInvalidateUserNameTooltip(collabUsersState, newLastLocalEditAt)) {
          // Then update the decorations
          const newDecorationData = createCursorDecorations(tr.doc, collabUsersState, collabUsersState.userMap, newLastLocalEditAt, options.document);
          newDecorations = DecorationSet.create(tr.doc, newDecorationData.decorations);
          newUserMap = newDecorationData.userMap;
        }
        // Else just map the decorations for the doc changes
        else {
          // Typically this case only happens on local edits but it can also happen on a collab edit that does not update the collab users data (eg the collab code editor)
          // We need to treat the collab edit case differently because the local doc version won't actually be changing with a collab edit since it already has the changes applied

          // Map the decorations to match the new doc
          newDecorations = collabUsersState.decorations.map(tr.mapping, tr.doc);

          // Map the user selections to match the new doc
          if (tr.mapping.maps.length > 0) {
            // There will be a collabState if this is a collab transaction
            const collabState = tr.getMeta('collab$');
            // If this is a collab transaction then use the doc version from the collab state because it already incorporates the steps in the transaction
            // For a local edit the new doc version is the current doc version + the number of steps in the transaction
            const newDocVersion = collabState?.version ?? getVersion(newEditorState) + tr.steps.length;
            newUserMap = mapCollabUserMap(collabUsersState, collabUsersState.userMap, tr.before, tr.doc, tr.mapping, newDocVersion);
          }
        }

        // Return a new state
        return {
          clientId: collabUsersState.clientId,
          decorations: newDecorations,
          lastLocalEditAt: newLastLocalEditAt,
          themeNumber: newThemeNumber,
          userId: collabUsersState.userId,
          userMap: newUserMap
        };
      }
    },

    props: {
      decorations(editorState: EditorState) {
        return this.getState(editorState).decorations;
      }
    },

    // Define a view to start and stop the timeout for the user state check
    view: () => {
      return {
        update(editorView: EditorView) {
          // Schedule the user state check. If the timeout is already scheduled then it won't be scheduled again
          scheduleUserStateCheck(editorView);
        },
        destroy() {
          clearTimeout(userStateCheckTimeoutId);
          userStateCheckTimeoutId = null;
        }
      };
    }
  });
}
